// Bilingual content. English is the default; Danish is opt-in via the toggle.
// Prose avoids em-dashes / en-dashes; only normal hyphens in legit compounds.

export const translations = {
  en: {
    seo: {
      title: "ReverseAI.dk | Spreadsheets, automation and management dashboards",
      description: "Business-critical spreadsheets are my specialty. I rebuild them as secure software and help B2B companies with process automation and management dashboards.",
    },
    businessPaths: {
      title: "Business-critical spreadsheets are my specialty.",
      intro: "I also help with the work around them: manual processes, disconnected systems and reporting that takes too long to put together.",
      specialty: "Core specialty",
      supporting: "Supporting service",
      items: [
        { title: "Spreadsheets", desc: "When pricing, quotes or calculations depend on a fragile file. Keep the business logic and move it into software your team can rely on.", cta: "See spreadsheet rebuilds" },
        { title: "Automation", desc: "When people spend time moving data, processing documents or chasing approvals. Connect the steps and use AI where it is useful.", cta: "See automation services" },
        { title: "Management dashboards", desc: "When the figures are spread across systems. Bring margins, orders and operational performance together in a clear overview.", cta: "See dashboard services" },
      ],
    },
    sectionVideo: {
      error: "The video could not be loaded.",
    },
    nav: {
      problem: "Problem",
      platform: "Services",
      proof: "Proof",
      specialist: "Services",
      security: "Security",
      process: "Process",
      cases: "Use cases",
      packages: "Packages",
      pricing: "Pricing",
      maintenance: "Maintenance",
      about: "About",
      faq: "FAQ",
      book: "Book a Spreadsheet Risk Audit",
    },
    hero: {
      pillTag: "Spreadsheet Risk Recovery",
      pillText: "Critical spreadsheets, rebuilt as secure software",
      headPre: "Your most important process runs on a spreadsheet. ",
      headHi: "That's the problem",
      headPost: ".",
      sub: "It controls your prices, margins and deliveries, and only one person fully understands it. I rebuild it into secure, validated software, without the numbers ever changing.",
      ctaPrimary: "Book a Spreadsheet Risk Audit",
      ctaSecondary: "See what I can build",
      credibility:
        "I reverse engineer Excel, build it into secure software, and take care of the development, hosting and AI around it.",
      scroll: "Scroll",
      videoPause: "Pause background video",
      videoPlay: "Play background video",
      proof: ["Same numbers as before", "Secure from the start", "EU-based and GDPR-ready"],
    },
    problem: {
      eyebrow: "The hidden risk",
      title: "When your most important system is a spreadsheet, it's a single point of failure.",
      intro:
        "A spreadsheet that has quietly become business-critical over the years is a black box. One person knows the logic. The file often decides pricing, margins and deliveries, with no documentation, access control or backup. And as long as the process stays undocumented and insecure, AI can't help you with any of it.",
      statText:
        "file often decides pricing, margins and deliveries, with no documentation, access control or backup logic.",
      risks: [
        { text: "One person understands the logic", tag: "key person risk" },
        { text: "Formulas break silently", tag: "silent failure" },
        { text: "No access control or audit trail", tag: "no governance" },
        { text: "Sensitive data sits unprotected", tag: "security risk" },
        { text: "Prices and numbers drift out of sync", tag: "inconsistent output" },
        { text: "AI cannot help on an undocumented, insecure process", tag: "blocked by chaos" },
      ],
    },
    specialist: {
      eyebrow: "Services",
      title: "Reliable systems for the work your business depends on.",
      intro:
        "My core work is rebuilding critical spreadsheets. I also help B2B teams automate manual work and get a clearer view of their business, using their existing systems wherever it makes sense.",
      appsNote:
        "You can start with the part that needs attention. An automation workflow or dashboard does not require a spreadsheet rebuild first. We agree on the data sources, scope and responsibilities before the work starts.",
      priceLabel: "Price",
      cta: "Tell me about your situation",
      addonTag: "Supporting service",
      specialtyTag: "Core specialty",
      cards: [
        {
          title: "Critical spreadsheets rebuilt as software",
          desc: "I take complex spreadsheets apart cell by cell and rebuild them as secure systems with encryption, access control and validation. If an app on top helps, I build that too.",
          points: ["Formula mapping", "Documented business rules", "Validated calculations"],
          cta: "Discuss your spreadsheet",
        },
        {
          title: "Process automation and AI workflows",
          desc: "I connect repetitive steps across your systems, from document handling to approvals and follow-up. Straightforward rules handle predictable tasks; AI helps where interpretation is needed, with people retaining control.",
          points: ["System connections", "Document handling", "Human approval"],
          cta: "Discuss your manual processes",
        },
        {
          title: "Security & compliance consulting",
          desc: "Threat modeling, MFA, role based access and audit ready logging, so your systems stay trustworthy and compliant.",
          points: ["Risk assessment", "Access control", "Audit logging"],
        },
        {
          title: "Management dashboards and business portals",
          optional: true,
          desc: "I bring data from your existing systems into dashboards for finance, sales and operations. We agree on the metrics and their sources, so your team can follow margins, order status and capacity without assembling another report by hand.",
          points: ["Agreed business metrics", "Connected data sources", "Role-based views"],
          cta: "Discuss your reporting needs",
        },
      ],
    },
    security: {
      eyebrow: "Security first",
      title: "Security is part of the build, not something I add at the end.",
      intro:
        "Good application security keeps systems up and saves you money, because catching a problem early is far cheaper than cleaning up after it. Every Excel rebuild I do comes with encryption, access control, logging and sensible DevOps.",
      auditEyebrow: "Spreadsheet Risk Audit",
      auditTitle: "What a Spreadsheet Risk Audit covers",
      checks: [
        { code: "AST", title: "Application security testing", desc: "Static and dynamic testing finds vulnerabilities before they reach production." },
        { code: "MFA", title: "Multi factor authentication", desc: "Strong sign in keeps accounts safe even if a password leaks." },
        { code: "RBAC", title: "Role based access control", desc: "Each person sees only the data and actions their role allows." },
        { code: "ENC", title: "Encryption at rest and in transit", desc: "Sensitive data stays protected on disk and across the network." },
      ],
      devsecopsTitle: "Security built in from the start",
      devsecopsIntro:
        "Security sits in every step of the pipeline instead of being tacked on at the end. You get safer releases and a clear trail for any audit.",
      benefits: [
        { title: "Less downtime", desc: "Issues are caught in the pipeline before your users ever see them." },
        { title: "Lower cost", desc: "Fixing a flaw early is far cheaper than recovering from a breach." },
        { title: "Compliance ready", desc: "Logging and access control make audits straightforward." },
        { title: "Something people trust", desc: "Customers and management can rely on a system they know is secure." },
      ],
      cta: "Book a Spreadsheet Risk Audit",
    },
    process: {
      eyebrow: "The process",
      title: "How a spreadsheet becomes a secure system.",
      intro:
        "A clear, structured process. You always know what is happening at each step, that the result matches your original logic, and that it is secured along the way.",
      steps: [
        { title: "Audit the spreadsheet", desc: "I review the file, identify critical cells, inputs, outputs and where the risk sits." },
        { title: "Map formulas and dependencies", desc: "Every formula and dependency is mapped into a dependency graph, including the hidden ones." },
        { title: "Extract business rules", desc: "The logic is translated into clear, documented business rules that can be tested." },
        { title: "Rebuild securely", desc: "The rules are rebuilt as a secure web app, calculation engine or AI workflow." },
        { title: "Validate and harden", desc: "The new system is validated against the original calculations and hardened with security controls." },
      ],
    },
    cases: {
      eyebrow: "Use cases",
      title: "Where the work makes a difference.",
      intro: "Examples of tasks I can help with, from a critical quote calculator to the weekly management report.",
      cta: "Discuss this task",
      items: [
        { path: "spreadsheets", title: "Product configurators", desc: "Product rules and dependencies moved out of Excel, with pricing and validation built into the tool." },
        { path: "spreadsheets", title: "Quote calculators", desc: "Documented calculations and consistent quotes without circulating different versions of the same file." },
        { path: "overview", title: "Margins and business metrics", desc: "A shared view of sales, costs and margins, with agreed definitions and traceable data sources." },
        { path: "automation", title: "Document handling", desc: "Extract information from incoming documents and route it for review before it reaches the next system." },
        { path: "overview", title: "Orders and capacity", desc: "Bring order status, delivery dates and capacity together so operations can see what needs attention." },
        { path: "automation", title: "Approvals and follow-up", desc: "Route requests, reminders and handovers without relying on someone to chase every step by email." },
        { path: "automation", title: "Internal knowledge search", desc: "Find procedures and answers in your own material, with source references and controlled access." },
        { path: "overview", title: "Management reporting", desc: "Use the same agreed figures across recurring reports, instead of collecting them from scratch each week." },
      ],
    },
    proof: {
      eyebrow: "Engineering case, anonymized",
      title: "Built from hidden logic and tested like real software.",
      intro:
        "An anonymized case that shows how a fragile spreadsheet ends up as a documented, secure system the business can rely on.",
      validated: "validated and secured",
      metricsLabels: ["formula edges mapped", "deviations after validation", "canonical inputs extracted", "calculation parity"],
      cards: [
        { title: "Formula logic mapped", desc: "Complex spreadsheet formulas, dependencies and exceptions are translated into documented system logic." },
        { title: "Secure access model", desc: "Role-based access, audit-ready structure and secure data handling replace uncontrolled spreadsheet sharing." },
        { title: "Calculation parity", desc: "The new system is validated against the original logic, so the business can trust the output." },
      ],
    },
    offer: {
      eyebrow: "Packages",
      title: "Three ways to get started.",
      intro: "From a focused secure audit to a full secure rebuild or an internal AI Workflow & Knowledge Engine. Choose the level that matches your situation.",
      priceLabel: "Price",
      note: "Larger systems, configurators, integrations and enterprise-class rebuilds are scoped individually after an assessment.",
      packages: [
        {
          name: "Spreadsheet Risk Audit",
          tag: "Start here",
          priceKey: "secureSystemAudit",
          desc: "For companies that need to understand risk, hidden spreadsheet logic, security exposure, automation potential and rebuild scope.",
          includes: ["Spreadsheet risk review", "Workflow review", "Security and system map", "Technical recommendation", "Implementation roadmap"],
          cta: "Book a Spreadsheet Risk Audit",
        },
        {
          name: "Secure System Rebuild",
          tag: "Most chosen",
          priceKey: "excelToSoftware",
          desc: "For companies with critical Excel configurators, pricing tools, quote calculators or operational spreadsheets that must become secure production systems.",
          includes: ["Reverse engineering", "Formulas and dependencies", "Business logic extraction", "Secure web app, configurator or dashboard", "Validation and parity testing", "Deployment plan"],
          cta: "Request System Rebuild",
        },
        {
          name: "AI Workflow & Knowledge Engine",
          tag: "Scale",
          priceKey: "aiWorkflows",
          desc: "For companies that want AI workflows, internal assistants, knowledge systems or controlled automation trained around their own processes.",
          includes: ["Custom prompts", "Knowledge base", "Internal assistant", "Workflow automations", "Human in the loop control", "Access control"],
          cta: "Request AI Workflow & Knowledge Engine",
        },
      ],
    },
    comparison: {
      eyebrow: "Packages & Pricing",
      title: "Choose the right path for your system.",
      intro:
        "An audit assesses spreadsheet risk, a rebuild turns critical logic into software, and the AI workflow service handles automation and internal knowledge. Automation can also connect to your current systems without a rebuild. Dashboard work is scoped separately under the services above.",
      featureCol: "What's included",
      bestForLabel: "Best for",
      optLabel: "Optional",
      columns: [
        {
          name: "Spreadsheet Risk Audit",
          tag: "Start here",
          cta: "Book audit",
          desc: "Diagnostic review of risk, hidden logic, security exposure and rebuild potential. Best before committing to a larger build.",
          bestFor: "Understanding risk, hidden logic and rebuild potential before committing.",
          tags: ["Risk review", "Logic map", "Security exposure", "Roadmap"],
          roi: "Less than the cost of finding the problem too late",
          creditNote: "Audit fee credited toward a later rebuild.",
        },
        {
          name: "Secure System Rebuild",
          tag: "Most chosen",
          cta: "Request rebuild",
          desc: "Full technical rebuild of critical spreadsheet logic, pricing rules, configurators or operational workflows into secure production software.",
          bestFor: "Critical spreadsheets, pricing tools, configurators and operational logic that must become secure software.",
          tags: ["Formula logic", "Secure software", "Validation parity", "Production rebuild"],
          roi: "Typically less than one serious pricing error, or one key person walking out",
        },
        {
          name: "AI Workflow & Knowledge Engine",
          tag: "Scale",
          cta: "Request AI workflow",
          desc: "Controlled AI workflows, internal assistants and company-specific knowledge systems built around existing processes.",
          bestFor: "Internal AI workflows, assistants, knowledge bases and controlled automation.",
          tags: ["AI workflows", "Knowledge base", "Internal assistant", "Human control"],
          roi: "Frees up hours every week in daily operations",
        },
      ],
      features: [
        "Risk and security audit",
        "Formula and dependency mapping",
        "Implementation roadmap",
        "Documented business rules",
        "Secure software rebuild",
        "Web app, configurator or dashboard",
        "Validation and parity testing",
        "Security hardening (RBAC, MFA, encryption)",
        "AI workflow design",
        "Internal AI assistant",
        "Knowledge base / RAG structure",
        "Human-in-the-loop controls",
        "Production deployment path",
        "Ongoing maintenance option",
      ],
      addonNote:
        "Standalone full-stack websites, dashboards and client portals are scoped separately. Simple premium builds can start from 100,000 DKK, while custom backend, admin systems, integrations and dashboards are quoted after assessment.",
      tiersNote:
        "Rebuild complexity: Standard (up to ~15 sheets) from 285,000 DKK · Advanced (complex dependencies, integrations, multiple roles) from 450,000 DKK · Enterprise by scope assessment.",
    },
    maintenance: {
      eyebrow: "Service & Maintenance",
      title: "Keep your system secure, updated and improving.",
      intro:
        "Once a system is live it still needs looking after: updates, monitoring, the odd small change and general technical care. Pick the level of support that fits how important the system is to you.",
      tiersLabel: "Monthly support plans",
      priceLabel: "Price",
      tiers: [
        {
          name: "Website Care",
          tag: "Light care",
          desc: "For websites, landing pages and smaller web platforms that need secure updates, monitoring and light ongoing care.",
          includes: ["Software updates", "Security patches", "Uptime monitoring", "Backup checks", "Small monthly edits", "Email support"],
        },
        {
          name: "System Care",
          tag: "Most chosen",
          desc: "For dashboards, admin systems, backend flows and custom web apps that need monthly technical care and improvements.",
          includes: ["Everything in Website Care", "Dependency and library updates", "Bug fixes", "Monthly improvement work", "Backend / API checks", "Performance monitoring", "Quarterly review"],
        },
        {
          name: "Mission-Critical Operations",
          tag: "Business critical",
          desc: "For business-critical systems, AI workflows, configurators, lead pipelines and secure platforms where downtime or errors matter.",
          includes: ["Everything in System Care", "Priority response", "Security hardening", "Advanced monitoring", "AI workflow monitoring", "Roadmap and feature work", "Dedicated point of contact"],
        },
      ],
      supportTitle: "Bigger systems need more support.",
      supportBody:
        "A simple website only needs a light touch. Dashboards, AI workflows and business-critical systems need closer monitoring, quicker response times and steady improvement over time. We settle on the right level once I have seen the system.",
      budgetNote:
        "As a rule of thumb, larger systems often reserve an annual maintenance budget equal to {pctMin}-{pctMax}% of the initial build cost, depending on complexity and SLA.",
      pctLabel: "Rule of thumb",
      pctNote: "of the initial build cost, per year",
      note: "These are 'from' prices. More complex systems, custom SLAs, high-traffic platforms and business-critical operations are scoped individually.",
      ctaPrimary: "Inquire about maintenance",
      cardCta: "Inquire about this plan",
      ctaSecondary: "Book a Spreadsheet Risk Audit",
    },
    pricing: {
      eyebrow: "Pricing & Scope",
      title: "Pricing based on assessment and scope",
      intro:
        "This is senior technical work: reverse engineering, secure software, AI workflows, validation and systems that are ready to run. The numbers below are starting points, and I scope each project properly before quoting.",
      priceLabel: "Price",
      addonTag: "Optional add-on",
      packages: [
        { name: "Spreadsheet Risk Audit", desc: "Technical review of spreadsheets, workflows, business logic, data risk, security exposure and rebuild potential. Includes a scoped recommendation and next step roadmap." },
        { name: "Secure System Rebuild", desc: "Reverse engineering of complex Excel logic, formulas, pricing rules, dependencies and business mechanisms, rebuilt into a secure software system, configurator, dashboard or web application." },
        { name: "AI Workflow & Knowledge Engine", desc: "Company specific AI workflows, internal assistants, process automation, document handling, structured prompts, integrations and controlled AI implementation." },
        { name: "Website & Dashboard Add-on", optional: true, desc: "Management dashboard, business portal or website connected to an existing or rebuilt system. Data sources, metrics and access are agreed as part of the scope." },
      ],
      noteMain: "Final pricing depends on the complexity of your spreadsheets or processes, data sources, integrations, security requirements, user roles and validation needs. Larger configurators, production systems and reporting solutions are quoted after assessment.",
      noteScope: "All engagements start with a free intro conversation. Full rebuilds require scope assessment before final pricing.",
      cta: "Book a Spreadsheet Risk Audit",
      ctaSecondary: "Tell me about your situation",
    },
    about: {
      eyebrow: "About the founder",
      title: "Someone who actually builds and runs the systems.",
      body:
        "I specialise in the spreadsheets that businesses have come to depend on. That work often extends into connecting systems, automating manual steps and giving management a clearer view of the numbers. I handle the technical work myself, from understanding the rules to building and maintaining the solution.",
      stack: ["AI and agents", "Reverse engineering", "Secure system design", "Web apps and APIs", "Server operations", "Security and compliance"],
      available: "available",
      role: "Founder, ReverseAI.dk",
      meta: [
        ["focus", "secure production systems"],
        ["region", "EU, remote or on site"],
        ["delivery", "audited, validated, hardened"],
      ],
      bottom: "Builds secure systems that run in production",
    },
    sequence: {
      eyebrow: "The transformation",
      title: "Watch a critical spreadsheet become a secure software system.",
      sub: "Watch the rebuild, one step at a time. It plays on its own.",
      share: "Share",
      shareCopied: "Link copied to clipboard",
      shareLinkedIn: "Share on LinkedIn",
      steps: [
        { label: "Source", caption: "47 sheets, undocumented. One person knows the logic." },
        { label: "Map", caption: "Every formula traced into a dependency graph." },
        { label: "Model", caption: "Logic becomes clean, typed, documented inputs." },
        { label: "Engine", caption: "Wrapped in a secure engine: encryption, access, audit." },
        { label: "Surface", caption: "A clean, role-based system. Same numbers, proven." },
      ],
      stats: [
        { label: "formula dependencies" },
        { label: "typed inputs" },
        { label: "deviations" },
        { label: "parity" },
      ],
      sourceTag: "47 sheets · undocumented",
      badges: ["AES-256", "RBAC", "MFA", "Audit log"],
      dashTitle: "Secure system · live",
    },
    busFactor: {
      eyebrow: "No lock-in",
      title: "\"What if you get hit by a bus?\"",
      body: "That's exactly the risk I remove, so it would be strange to hand you a new one. You own the full source code and the documentation, there's no lock-in, and every system is handed over so your own team, or any developer, can run it without me. You don't swap one key-person risk for another. You retire it.",
      points: ["Full source code is yours", "Complete documentation included", "Defined handover, no dependency on me"],
    },
    finalCta: {
      eyebrow: "One last question",
      title: "Do you have a spreadsheet that controls more of the company than it should?",
      sub: "Let us take a look at it together. A Spreadsheet Risk Audit gives you a clear picture of the risk, where the gaps are and what to do next.",
      book: "Book a Spreadsheet Risk Audit",
      contact: "Tell me about your situation",
    },
    contact: {
      eyebrow: "Contact",
      title: "Let us look at your system.",
      intro:
        "Tell me about the spreadsheet you depend on, the manual task taking too much time, or the overview you are missing. You do not need a finished specification. I will get back to you with a concrete next step.",
      bookCardTitle: "Prefer to talk right away?",
      bookCardSub: "Book a free 30-minute intro about your spreadsheet, processes or reporting.",
      bookBtn: "Book",
      labels: { name: "Name", email: "Email", company: "Company", pkg: "Service", message: "Message" },
      placeholders: { name: "Your name", email: "you@company.com", company: "Company name", message: "What is difficult today, which systems are involved, and what would you like to change?" },
      selectDefault: "Select (optional)",
      otherOption: "Other or not sure yet",
      submit: "Send request",
      sending: "Sending...",
      successTitle: "Thanks for your message",
      successSub: "I will get back to you as soon as possible.",
      sendAnother: "Send another",
      footnote: "You will hear from me within 1 to 2 business days.",
      errRequired: "Please fill in name, email and message.",
      errFail: "Something went wrong. Please try again or book an audit directly.",
      successToast: "Thanks! Your request has been sent. I will get back to you shortly.",
    },
    footer: {
      tagline:
        "Critical spreadsheets are my specialty. Secure software rebuilds, process automation and management dashboards for B2B companies, with development and ongoing maintenance handled together.",
      navHeading: "Navigation",
      getStarted: "Get started",
      book: "Book a Spreadsheet Risk Audit",
      writeToMe: "Write to me",
      role: "Secure Systems & AI Consulting",
    },
    trustbar: {
      label: "Built for operations teams in",
      industries: ["Manufacturing", "B2B suppliers", "Engineering", "SaaS & tech", "Wholesale & distribution"],
      points: ["EU-based, GDPR-ready", "NDA on request", "Validated to 100% parity", "Secure from the start"],
    },
    transformation: {
      eyebrow: "Before and after",
      title: "From a fragile spreadsheet to a system you can trust.",
      intro:
        "It is the same business logic, just rebuilt so it is documented, secure and tested. Here is what actually changes for your team.",
      beforeLabel: "Today: critical spreadsheet",
      afterLabel: "After: secure system",
      before: [
        "Logic lives in one person's head",
        "Silent formula errors reach customers",
        "No access control or audit trail",
        "Sensitive data sits unprotected",
        "Slow, manual quoting and pricing",
        "AI cannot safely touch the process",
      ],
      after: [
        "Documented logic the whole team can rely on",
        "Validated calculations, errors caught before release",
        "Role based access and a full audit trail",
        "Encryption at rest and in transit",
        "Fast, consistent quotes and pricing",
        "A clean foundation AI can build on",
      ],
    },
    liveTransform: {
      eyebrow: "Live system transformation",
      title: "Watch a critical spreadsheet become a secure software system.",
      intro:
        "This is the same process I run on real projects, shown from start to finish: the hidden formulas in a spreadsheet are mapped out, turned into a clean input model, rebuilt as a secure engine, and finally given a dashboard your team can trust.",
      live: "live",
      videoSub: "Spreadsheet logic turned into a secure system",
      metricsTitle: "Validated, not assumed",
      stages: [
        { code: "01 source", title: "Spreadsheet formulas", desc: "Undocumented cells, nested formulas and hidden logic.", metric: "47 sheets" },
        { code: "02 map", title: "Dependency graph", desc: "Every formula and reference mapped, including the hidden ones.", metric: "1,240 edges" },
        { code: "03 model", title: "Canonical input model", desc: "Inputs typed, validated and de-duplicated into one clean model.", metric: "62 typed inputs" },
        { code: "04 engine", title: "Secure software engine", desc: "Business rules rebuilt with encryption, access control and validation.", metric: "AES-256 RBAC" },
        { code: "05 surface", title: "Dashboard & admin layer", desc: "A secure, role based interface your whole team can rely on.", metric: "100% parity" },
      ],
      metrics: [
        { value: "1,240", label: "formula edges mapped" },
        { value: "62", label: "canonical inputs" },
        { value: "0", label: "deviations after validation" },
        { value: "100%", label: "calculation parity" },
      ],
    },
    testimonials: {
      eyebrow: "What clients say",
      title: "Trusted with business-critical systems, and validated like real software.",
      intro:
        "Anonymized feedback from real engagements. Names are withheld by agreement, the work and the numbers speak for themselves.",
      note: "Client names withheld under NDA.",
      items: [
        { subhead: "Production configurator · Manufacturing (~200 employees)", metric: "47 sheets · 1,240 formula dependencies · 62 typed inputs · 0 deviations · 100% parity", quote: "Our pricing logic lived in one enormous spreadsheet only one person fully understood. It's now a secure system the whole team can rely on.", role: "Operations Director", company: "name under NDA" },
        { subhead: "B2B supplier", metric: "Role-based access · Full audit trail · In production", quote: "We finally have access control and an audit trail on the numbers that run the business.", role: "CFO", company: "name under NDA" },
        { subhead: "Wholesale & distribution", metric: "Audit-ready · Secure data handling · Live", quote: "Straight, technical and honest. No buzzword AI demo, just a working system in production our auditors could sign off on.", role: "Head of IT", company: "name under NDA" },
      ],
    },
    faq: {
      eyebrow: "FAQ",
      title: "Questions companies ask before we start.",
      intro: "If your question is not here, write to me and you will get a straight answer.",
      items: [
        { q: "Do we own the code and the system?", a: "Yes. You own the full source code, the documentation and the rebuilt system. There is no lock-in, so you can take it to any developer." },
        { q: "Is everything kept confidential?", a: "Yes. Everything is handled confidentially, and I am happy to sign an NDA before you share any files or data." },
        { q: "How do I know the new system matches my spreadsheet?", a: "Every rebuild is validated against your original calculations with a parity test suite. You see the proof: 100% parity, 0 deviations, before anything goes live." },
        { q: "How secure is it really?", a: "Pretty secure. Encryption at rest and in transit, multi-factor login, role-based access and audit-ready logging as standard." },
        { q: "What does it actually cost?", a: "An audit from 35,000 DKK (credited toward a rebuild). A full rebuild from 285,000 DKK depending on complexity. You get a concrete scope and price after the audit, no surprises." },
        { q: "How long does a project take?", a: "An audit is quick. A full rebuild depends on complexity, but you get a clear scope, timeline and price first." },
        { q: "What happens to my data and GDPR?", a: "I am EU based and build with GDPR in mind: data minimization, access control and clear handling." },
        { q: "What if you get hit by a bus?", a: "You own the source code and documentation, and the system is handed over so your team or any developer can carry on without me." },
        { q: "What if I am not sure what I need yet?", a: "Start with the free intro conversation. We look at your situation together and you get a concrete recommendation." },
        { q: "Can you help with automation or dashboards without rebuilding a spreadsheet?", a: "Yes. Critical spreadsheets are my specialty, but I also take on automation and management dashboard work around your existing systems. We start with the task, the available data and who will use the result, then agree on the scope. A spreadsheet rebuild is not a prerequisite." },
      ],
    },
    leadMagnet: {
      eyebrow: "Free resource",
      title: "Get the Spreadsheet Risk Checklist.",
      intro:
        "A practical checklist to spot key person risk, silent errors, security gaps and rebuild potential in your most critical spreadsheets. Sent straight to your inbox.",
      bullets: ["12 checks across logic, security and continuity", "No sign up, just your email", "Written for operations and finance teams"],
      placeholder: "you@company.com",
      companyPlaceholder: "Company (optional)",
      submit: "Send me the checklist",
      sending: "Sending...",
      successTitle: "Check your inbox",
      successSub: "The Spreadsheet Risk Checklist is on its way. If you do not see it, check your spam folder.",
      errEmail: "Please enter a valid email.",
      errFail: "Something went wrong. Please try again.",
      privacyNote: "We use your email only to send the checklist and occasional relevant follow up. Unsubscribe anytime.",
    },
    privacy: {
      title: "Privacy and cookie policy",
      updated: "Last updated June 2026",
      back: "Back to site",
      sections: [
        { h: "Who we are", p: "ReverseAI.dk is a one person technical consultancy based in the EU, run by Peter Hald. You can reach us through the contact form on this site." },
        { h: "What we collect", p: "When you submit the contact form or request the checklist we collect the name, email, company and message you provide. We also collect anonymous usage analytics, but only if you accept analytics cookies." },
        { h: "Why we collect it", p: "To respond to your enquiry, deliver the resource you requested and improve the website. We never sell your data." },
        { h: "Cookies and analytics", p: "Necessary cookies keep the site working, for example your language choice. Optional analytics (PostHog) only run after you accept them in the cookie banner. You can decline without losing any functionality." },
        { h: "Email", p: "Lead and checklist emails are sent via Resend. We use your email only to reply or to send what you requested. You can ask us to delete your data at any time." },
        { h: "Your rights", p: "Under GDPR you can request access to, correction of, or deletion of your personal data. Contact us through the form and we will act promptly." },
      ],
    },
    consent: {
      title: "We value your privacy",
      body: "We use necessary cookies to run the site and optional analytics (including PostHog usage insights) to understand how the site is used and improve it. You decide on analytics.",
      accept: "Accept",
      reject: "Decline",
      necessaryNote: "Necessary cookies are always on.",
      privacy: "Privacy and cookies",
    },
    audit: {
      badge: "Spreadsheet Risk Audit",
      headline: "Book a Spreadsheet Risk Audit",
      sub: "Tell us about the spreadsheet, workflow or business logic that controls more of your company than it should.",
      trust: ["Confidential, NDA on request", "EU-based, GDPR-ready", "No obligation"],
      labels: { name: "Name", email: "Email", company: "Company", phone: "Phone", systemType: "System type", criticality: "Business criticality", budget: "Budget range", pkg: "Package interest", message: "Message" },
      optional: "optional",
      placeholders: { name: "Your name", email: "you@company.com", company: "Company name", phone: "+45 ...", message: "Briefly describe the spreadsheet, workflow, configurator, pricing tool or system you want assessed." },
      selectDefault: "Select",
      systemTypes: [
        { value: "excel_spreadsheet", label: "Excel spreadsheet" },
        { value: "pricing_calculator", label: "Pricing calculator" },
        { value: "product_configurator", label: "Product configurator" },
        { value: "quote_tool", label: "Quote tool" },
        { value: "manual_workflow", label: "Manual workflow" },
        { value: "ai_workflow_automation", label: "AI / workflow automation" },
        { value: "other", label: "Other" },
      ],
      criticality: [
        { value: "nice_to_have", label: "Nice to have" },
        { value: "important", label: "Important" },
        { value: "critical", label: "Critical to pricing/sales/operations" },
      ],
      budget: [
        { value: "below_25k", label: "Below 25,000 DKK" },
        { value: "25k_75k", label: "25,000-75,000 DKK" },
        { value: "75k_200k", label: "75,000-200,000 DKK" },
        { value: "200k_plus", label: "200,000+ DKK" },
        { value: "not_sure", label: "Not sure yet" },
      ],
      submit: "Request Spreadsheet Risk Audit",
      sending: "Sending...",
      successTitle: "Request received.",
      successBody: "I will review your system details and reply with the next best step.",
      successNext: "If the project is a fit, the next step is a 30-minute intro call or a scoped Spreadsheet Risk Audit.",
      successHome: "Back to ReverseAI.dk",
      back: "Back to site",
      footnote: "Everything you share is treated confidentially. You will hear from me within 1 to 2 business days.",
      errRequired: "Please fill in all required fields.",
      errEmail: "Please enter a valid email.",
      errMessage: "Please describe your system in a bit more detail (at least 20 characters).",
      errFail: "Something went wrong. Please try again.",
    },
    booking: {
      badge: "Book a time",
      headline: "Book a 30-minute call",
      sub: "Pick a time that works for you and get an instant confirmation by email. No back and forth.",
      trust: ["30 minutes, no obligation", "Confidential, NDA on request", "EU-based, GDPR-ready"],
      auditPrompt: "Prefer to just book a 30-min call?",
      steps: { date: "1. Pick a date", time: "2. Pick a time", details: "3. Your details" },
      selectDatePrompt: "Select a date to see available times.",
      loadingSlots: "Loading times...",
      noSlots: "No available times on this day. Try another weekday.",
      slotsLabel: "Available times (Danish time)",
      labels: { name: "Name", email: "Email", company: "Company", phone: "Phone", meetingType: "Meeting type", message: "Anything I should know?" },
      placeholders: { name: "Your name", email: "you@company.com", company: "Company name", phone: "+45 ...", message: "Briefly, what would you like to talk about?" },
      meetingTypes: [
        { value: "phone", label: "Phone" },
        { value: "zoom", label: "Zoom" },
        { value: "teams", label: "Microsoft Teams" },
      ],
      optional: "optional",
      submit: "Confirm booking",
      sending: "Booking...",
      successTitle: "You're booked",
      successBody: "Your time is reserved and a confirmation is on its way to your inbox. I will reach out at the agreed time.",
      successWhen: "Time",
      successType: "Meeting type",
      successHome: "Back to ReverseAI.dk",
      back: "Back to site",
      footnote: "All times are Danish time (Europe/Copenhagen).",
      errRequired: "Please fill in your name and email.",
      errEmail: "Please enter a valid email.",
      errSlot: "Please pick a date and time.",
      errType: "Please choose a meeting type.",
      errTaken: "That time was just taken. Please pick another slot.",
      errFail: "Something went wrong. Please try again.",
    },
  },

  da: {
    seo: {
      title: "ReverseAI.dk | Regneark, automatisering og ledelsesoverblik",
      description: "Kritiske regneark er mit speciale. Jeg bygger dem om til sikker software og hjælper B2B-virksomheder med automatisering og ledelsesoverblik.",
    },
    businessPaths: {
      title: "Kritiske regneark er mit speciale.",
      intro: "Jeg hjælper også med arbejdet omkring dem: manuelle processer, systemer der ikke hænger sammen, og rapportering der tager for lang tid at samle.",
      specialty: "Hovedspeciale",
      supporting: "Supplerende spor",
      items: [
        { title: "Regneark", desc: "Når priser, tilbud eller beregninger afhænger af en skrøbelig fil. Bevar forretningslogikken, og flyt den ind i software, teamet kan stole på.", cta: "Se regnearksløsninger" },
        { title: "Automatisering", desc: "Når tiden går med at flytte data, behandle dokumenter eller jagte godkendelser. Forbind arbejdsgangene, og brug AI, hvor det giver mening.", cta: "Se automatisering" },
        { title: "Ledelsesoverblik", desc: "Når tallene ligger spredt på tværs af systemer. Saml marginer, ordrer og driftens nøgletal i et overblik, I kan handle på.", cta: "Se dashboards og overblik" },
      ],
    },
    sectionVideo: {
      error: "Videoen kunne ikke indlæses.",
    },
    nav: {
      problem: "Problem",
      platform: "Ydelser",
      proof: "Bevis",
      specialist: "Services",
      security: "Sikkerhed",
      process: "Proces",
      cases: "Cases",
      packages: "Pakker",
      pricing: "Priser",
      maintenance: "Vedligeholdelse",
      about: "Om",
      faq: "FAQ",
      book: "Book en Spreadsheet Risk Audit",
    },
    hero: {
      pillTag: "Spreadsheet Risk Recovery",
      pillText: "Kritiske regneark, bygget om til sikker software",
      headPre: "Din vigtigste proces kører i et regneark. ",
      headHi: "Det er problemet",
      headPost: ".",
      sub: "Det styrer dine priser, marginer og leverancer, og kun én person forstår det helt. Jeg bygger det om til sikker, valideret software, uden at tallene ændrer sig.",
      ctaPrimary: "Book en Spreadsheet Risk Audit",
      ctaSecondary: "Se hvad jeg kan bygge",
      credibility:
        "Jeg reverse engineerer Excel, bygger det om til sikker software og står for udviklingen, driften og AI'en omkring det.",
      scroll: "Scroll",
      videoPause: "Sæt baggrundsvideo på pause",
      videoPlay: "Afspil baggrundsvideo",
      proof: ["Samme tal som før", "Sikkert fra starten", "EU-baseret og GDPR-klar"],
    },
    problem: {
      eyebrow: "Den skjulte risiko",
      title: "Når dit vigtigste system er et regneark, er det et single point of failure.",
      intro:
        "Et regneark, der stille og roligt er blevet forretningskritisk gennem årene, er en sort boks. Én person kender logikken. Filen afgør ofte priser, marginer og leverancer, uden dokumentation, adgangskontrol eller backup. Og så længe processen er udokumenteret og usikker, kan AI ikke hjælpe dig med noget som helst.",
      statText:
        "fil afgør ofte prissætning, marginer og leverancer, uden dokumentation, adgangskontrol eller backup logik.",
      risks: [
        { text: "Én person forstår logikken", tag: "nøgleperson risiko" },
        { text: "Formler bryder i stilhed", tag: "skjult fejl" },
        { text: "Ingen adgangskontrol eller revisionsspor", tag: "ingen styring" },
        { text: "Følsomme data ligger ubeskyttet", tag: "sikkerhedsrisiko" },
        { text: "Priser og tal kommer ud af sync", tag: "inkonsistent output" },
        { text: "AI kan ikke hjælpe på en udokumenteret, usikker proces", tag: "blokeret af kaos" },
      ],
    },
    specialist: {
      eyebrow: "Ydelser",
      title: "Systemer til det arbejde, virksomheden er afhængig af.",
      intro:
        "Mit hovedspeciale er at bygge kritiske regneark om til sikker software. Jeg hjælper også B2B-virksomheder med at automatisere manuelle opgaver og få bedre overblik, med udgangspunkt i de systemer, I allerede bruger.",
      appsNote:
        "I kan starte med den del, der trænger. Automatisering eller et dashboard kræver ikke, at et regneark først bliver bygget om. Vi aftaler datakilder, omfang og ansvar, inden arbejdet går i gang.",
      priceLabel: "Pris",
      cta: "Skriv kort om din situation",
      addonTag: "Supplerende spor",
      specialtyTag: "Hovedspeciale",
      cards: [
        {
          title: "Kritiske regneark bygget om til software",
          desc: "Jeg piller komplekse regneark fra hinanden celle for celle og bygger dem op som sikre systemer med kryptering, adgangskontrol og validering. Hjælper en app ovenpå, bygger jeg også den.",
          points: ["Kortlægning af formler", "Dokumenterede forretningsregler", "Validerede beregninger"],
          cta: "Tal om jeres regneark",
        },
        {
          title: "Procesautomatisering og AI-workflows",
          desc: "Jeg forbinder gentagne opgaver på tværs af jeres systemer, fra dokumenthåndtering til godkendelser og opfølgning. Faste regler klarer det forudsigelige; AI hjælper, når indhold skal fortolkes, mens mennesker bevarer kontrollen.",
          points: ["Sammenkobling af systemer", "Dokumenthåndtering", "Menneskelig godkendelse"],
          cta: "Tal om jeres manuelle processer",
        },
        {
          title: "Sikkerhed og compliance-rådgivning",
          desc: "Trusselsvurdering, multifaktor-login, rollebaseret adgang og revisionsklar logning, så I har styr på systemernes sikkerhed og dokumentation.",
          points: ["Risikovurdering", "Adgangskontrol", "Revisionslogning"],
        },
        {
          title: "Ledelsesoverblik, dashboards og portaler",
          optional: true,
          desc: "Jeg samler data fra jeres eksisterende systemer i dashboards til økonomi, salg og drift. Vi aftaler nøgletal og datakilder, så I kan følge marginer, ordrestatus og kapacitet uden at samle endnu en rapport i hånden.",
          points: ["Fælles nøgletal", "Sammenhængende datakilder", "Rollebaseret overblik"],
          cta: "Tal om jeres rapportering",
        },
      ],
    },
    security: {
      eyebrow: "Sikkerhed først",
      title: "Sikkerhed er en del af opbygningen, ikke noget jeg sætter på til sidst.",
      intro:
        "God applikationssikkerhed holder systemerne kørende og sparer dig penge, for det er langt billigere at fange et problem tidligt end at rydde op bagefter. Hver Excel-rebuild, jeg laver, kommer med kryptering, adgangskontrol, logging og fornuftig DevOps.",
      auditEyebrow: "Spreadsheet Risk Audit",
      auditTitle: "Hvad en Spreadsheet Risk Audit dækker",
      checks: [
        { code: "AST", title: "Application security testing", desc: "Statisk og dynamisk test finder sårbarheder, før de når i produktion." },
        { code: "MFA", title: "Multifaktor login", desc: "Stærkt login holder konti sikre, selv hvis en adgangskode lækker." },
        { code: "RBAC", title: "Rollebaseret adgangskontrol", desc: "Hver person ser kun de data og handlinger, deres rolle tillader." },
        { code: "ENC", title: "Kryptering i hvile og i transit", desc: "Følsomme data forbliver beskyttet på disk og over netværket." },
      ],
      devsecopsTitle: "Sikkerhed bygget ind fra start",
      devsecopsIntro:
        "Sikkerheden sidder i hvert trin af pipelinen i stedet for at blive klistret på til sidst. Det giver sikrere releases og et klart spor til en eventuel revision.",
      benefits: [
        { title: "Mindre nedetid", desc: "Fejl fanges i pipelinen, før jeres brugere ser dem." },
        { title: "Lavere omkostning", desc: "At rette en fejl tidligt er langt billigere end at komme sig efter et brud." },
        { title: "Klar til compliance", desc: "Logging og adgangskontrol gør audits enkle." },
        { title: "Noget folk stoler på", desc: "Kunder og ledelse kan stole på et system, de ved er sikkert." },
      ],
      cta: "Book en Spreadsheet Risk Audit",
    },
    process: {
      eyebrow: "Processen",
      title: "Sådan bliver et regneark til et sikkert system.",
      intro:
        "En klar, struktureret proces. I ved altid, hvad der sker i hvert trin, at resultatet matcher jeres oprindelige logik, og at det sikres undervejs.",
      steps: [
        { title: "Audit af regnearket", desc: "Jeg gennemgår filen, identificerer kritiske celler, inputs, outputs og hvor risikoen ligger." },
        { title: "Kortlæg formler og afhængigheder", desc: "Hver formel og afhængighed kortlægges i en dependency graf, også de skjulte." },
        { title: "Udtræk forretningsregler", desc: "Logikken oversættes til klare, dokumenterede forretningsregler, der kan testes." },
        { title: "Genopbyg sikkert", desc: "Reglerne genopbygges som en sikker web app, beregningsmotor eller AI workflow." },
        { title: "Validér og hærd", desc: "Det nye system valideres mod de oprindelige beregninger og hærdes med sikkerhedskontroller." },
      ],
    },
    cases: {
      eyebrow: "Eksempler på opgaver",
      title: "Her kan arbejdet gøre en forskel.",
      intro: "Eksempler på det, jeg kan hjælpe med, fra den kritiske tilbudsberegner til den ugentlige ledelsesrapport.",
      cta: "Tal om denne opgave",
      items: [
        { path: "spreadsheets", title: "Produktkonfiguratorer", desc: "Produktregler og afhængigheder flyttet ud af Excel, med prisberegning og validering bygget ind i værktøjet." },
        { path: "spreadsheets", title: "Tilbudsberegnere", desc: "Dokumenterede beregninger og ensartede tilbud uden forskellige versioner af den samme fil i omløb." },
        { path: "overview", title: "Marginer og nøgletal", desc: "Fælles overblik over salg, omkostninger og marginer med aftalte definitioner og sporbare datakilder." },
        { path: "automation", title: "Dokumenthåndtering", desc: "Udtræk oplysninger fra indgående dokumenter, og send dem til godkendelse, før de går videre til næste system." },
        { path: "overview", title: "Ordrer og kapacitet", desc: "Saml ordrestatus, leveringsdatoer og kapacitet, så driften kan se, hvad der kræver opmærksomhed." },
        { path: "automation", title: "Godkendelser og opfølgning", desc: "Send forespørgsler, påmindelser og overdragelser videre uden at skulle følge hvert trin op på mail." },
        { path: "automation", title: "Søgning i intern viden", desc: "Find procedurer og svar i jeres eget materiale med kildehenvisninger og styret adgang." },
        { path: "overview", title: "Ledelsesrapportering", desc: "Brug de samme aftalte tal i de tilbagevendende rapporter, frem for at samle dem fra bunden hver uge." },
      ],
    },
    proof: {
      eyebrow: "Teknisk case, anonymiseret",
      title: "Bygget fra skjult logik og testet som rigtig software.",
      intro:
        "En anonymiseret case, der viser, hvordan et skrøbeligt regneark ender som et dokumenteret, sikkert system, forretningen kan stole på.",
      validated: "valideret og sikret",
      metricsLabels: ["formel forbindelser kortlagt", "afvigelser efter validering", "kanoniske inputs udtrukket", "beregningsmæssig parity"],
      cards: [
        { title: "Formel-logik kortlagt", desc: "Komplekse formler, afhængigheder og undtagelser bliver oversat til dokumenteret systemlogik." },
        { title: "Sikker adgangsmodel", desc: "Rollebaseret adgang, audit-klar struktur og sikker datahåndtering erstatter ukontrolleret deling af regneark." },
        { title: "Beregnings-paritet", desc: "Det nye system valideres mod den oprindelige logik, så virksomheden kan stole på outputtet." },
      ],
    },
    offer: {
      eyebrow: "Pakker",
      title: "Tre måder at komme i gang på.",
      intro: "Fra en fokuseret audit til en fuld sikker rebuild eller en intern AI Workflow & Knowledge Engine. Vælg det niveau, der matcher jeres situation.",
      priceLabel: "Pris",
      note: "Større systemer, konfiguratorer, integrationer og rebuilds i enterprise klasse scopes individuelt efter en vurdering.",
      packages: [
        {
          name: "Spreadsheet Risk Audit",
          tag: "Start her",
          priceKey: "secureSystemAudit",
          desc: "For virksomheder, der har brug for at forstå risiko, skjult regnearkslogik, sikkerhedseksponering, automatiseringspotentiale og rebuild omfang.",
          includes: ["Risiko gennemgang af regneark", "Workflow gennemgang", "Sikkerheds og system map", "Teknisk anbefaling", "Implementeringskøreplan"],
          cta: "Book en Spreadsheet Risk Audit",
        },
        {
          name: "Secure System Rebuild",
          tag: "Mest valgt",
          priceKey: "excelToSoftware",
          desc: "For virksomheder med kritiske Excel konfiguratorer, prisværktøjer, tilbudsberegnere eller driftsregneark, der skal blive til sikre produktionssystemer.",
          includes: ["Reverse engineering", "Formler og afhængigheder", "Udtræk af forretningslogik", "Sikker web app, konfigurator eller dashboard", "Validering og parity test", "Deployment plan"],
          cta: "Anmod om System Rebuild",
        },
        {
          name: "AI Workflow & Knowledge Engine",
          tag: "Skalér",
          priceKey: "aiWorkflows",
          desc: "For virksomheder, der vil have AI workflows, interne assistenter, vidensystemer eller kontrolleret automation trænet omkring deres egne processer.",
          includes: ["Custom prompts", "Knowledge base", "Intern assistent", "Workflow automationer", "Human in the loop kontrol", "Adgangskontrol"],
          cta: "Anmod om AI Workflow & Knowledge Engine",
        },
      ],
    },
    comparison: {
      eyebrow: "Pakker og priser",
      title: "Vælg det rigtige spor til dit system.",
      intro:
        "En audit afdækker regnearksrisiko, et rebuild gør kritisk logik til software, og AI-workflow-ydelsen håndterer automatisering og intern viden. Automatisering kan også kobles til jeres nuværende systemer uden et rebuild. Dashboard-opgaver afgrænses særskilt under ydelserne ovenfor.",
      featureCol: "Hvad er inkluderet",
      bestForLabel: "Bedst til",
      optLabel: "Valgfrit",
      columns: [
        {
          name: "Spreadsheet Risk Audit",
          tag: "Start her",
          cta: "Book audit",
          desc: "Diagnostisk gennemgang af risiko, skjult logik, sikkerhedseksponering og rebuild-potentiale. Bedst før du forpligter dig til et større build.",
          bestFor: "Forstå risiko, skjult logik og rebuild-potentiale, før du forpligter dig.",
          tags: ["Risikogennemgang", "Logik-map", "Sikkerhedseksponering", "Køreplan"],
          roi: "Mindre end omkostningen ved at opdage problemet for sent",
          creditNote: "Audit-gebyret modregnes i et efterfølgende rebuild.",
        },
        {
          name: "Secure System Rebuild",
          tag: "Mest valgt",
          cta: "Forespørg rebuild",
          desc: "Fuld teknisk rebuild af kritisk regnearkslogik, prisregler, konfiguratorer eller driftsworkflows til sikker produktionssoftware.",
          bestFor: "Kritiske regneark, prisværktøjer, konfiguratorer og driftslogik der skal blive til sikker software.",
          tags: ["Formel-logik", "Sikker software", "Validering & parity", "Produktions-rebuild"],
          roi: "Typisk mindre end én alvorlig prisfejl, eller én nøgleperson der siger op",
        },
        {
          name: "AI-workflow & vidensmotor",
          tag: "Skalér",
          cta: "Forespørg AI-workflow",
          desc: "Kontrollerede AI-workflows, interne assistenter og virksomhedsspecifikke videnssystemer bygget omkring eksisterende processer.",
          bestFor: "Interne AI-workflows, assistenter, vidensbaser og kontrolleret automatisering.",
          tags: ["AI-workflows", "Vidensbase", "Intern assistent", "Menneskelig kontrol"],
          roi: "Frigør timer hver uge i den daglige drift",
        },
      ],
      features: [
        "Risiko- og sikkerhedsaudit",
        "Kortlægning af formler og afhængigheder",
        "Implementeringskøreplan",
        "Dokumenterede forretningsregler",
        "Sikker software-rebuild",
        "Web app, konfigurator eller dashboard",
        "Validering og parity-test",
        "Sikkerhedshærdning (RBAC, MFA, kryptering)",
        "Design af AI-workflow",
        "Intern AI-assistent",
        "Vidensbase / RAG-struktur",
        "Human-in-the-loop kontrol",
        "Vej til produktion (deployment)",
        "Mulighed for løbende vedligehold",
      ],
      addonNote:
        "Standalone full-stack websites, dashboards og klientportaler scopes separat. Enkle premium-builds kan starte fra 100.000 DKK, mens custom backend, adminsystemer, integrationer og dashboards prissættes efter vurdering.",
      tiersNote:
        "Rebuild-kompleksitet: Standard (op til ~15 ark) fra 285.000 kr. · Advanced (komplekse afhængigheder, integrationer, flere roller) fra 450.000 kr. · Enterprise efter scope-vurdering.",
    },
    maintenance: {
      eyebrow: "Service & Vedligeholdelse",
      title: "Hold dit system sikkert, opdateret og i udvikling.",
      intro:
        "Når et system først er live, skal det stadig passes: opdateringer, overvågning, en mindre ændring i ny og næ og almindelig teknisk vedligeholdelse. Vælg det supportniveau, der passer til, hvor vigtigt systemet er for jer.",
      tiersLabel: "Månedlige supportplaner",
      priceLabel: "Pris",
      tiers: [
        {
          name: "Website Care",
          tag: "Let vedligehold",
          desc: "Til hjemmesider, landingpages og mindre webplatforme, der kræver sikre opdateringer, overvågning og let løbende vedligehold.",
          includes: ["Softwareopdateringer", "Sikkerhedspatches", "Oppetidsovervågning", "Backup-kontrol", "Mindre månedlige rettelser", "Email-support"],
        },
        {
          name: "System Care",
          tag: "Mest valgt",
          desc: "Til dashboards, adminsystemer, backend-flows og custom webapps, der kræver månedlig teknisk vedligehold og forbedring.",
          includes: ["Alt i Website Care", "Dependency- og library-opdateringer", "Bugfixes", "Månedlige forbedringer", "Backend / API-kontrol", "Performance-overvågning", "Kvartalsvis review"],
        },
        {
          name: "Mission-Critical Operations",
          tag: "Forretningskritisk",
          desc: "Til forretningskritiske systemer, AI-workflows, konfiguratorer, lead-pipelines og sikre platforme, hvor nedetid eller fejl har reel betydning.",
          includes: ["Alt i System Care", "Prioriteret respons", "Security hardening", "Avanceret overvågning", "AI-workflow monitorering", "Roadmap- og featurearbejde", "Dedikeret kontaktperson"],
        },
      ],
      supportTitle: "Større systemer kræver mere support.",
      supportBody:
        "En simpel hjemmeside kræver kun lidt. Dashboards, AI-workflows og forretningskritiske systemer kræver tættere overvågning, hurtigere svartider og løbende forbedring. Vi finder det rigtige niveau, når jeg har set systemet.",
      budgetNote:
        "Som tommelfingerregel reserverer større systemer ofte et årligt vedligeholdelsesbudget på {pctMin}-{pctMax}% af første build-pris, afhængigt af kompleksitet og SLA.",
      pctLabel: "Tommelfingerregel",
      pctNote: "af første build-pris, pr. år",
      note: "Dette er 'fra'-priser. Mere komplekse systemer, custom SLA'er, high-traffic platforme og forretningskritisk drift prissættes individuelt.",
      ctaPrimary: "Spørg til vedligehold",
      cardCta: "Spørg til denne plan",
      ctaSecondary: "Book en Spreadsheet Risk Audit",
    },
    pricing: {
      eyebrow: "Priser og omfang",
      title: "Priser baseret på vurdering og omfang",
      intro:
        "Det her er teknisk arbejde på seniorniveau: reverse engineering, sikker software, AI-workflows, validering og systemer, der er klar til drift. Tallene nedenfor er udgangspunkter, og jeg scoper hvert projekt ordentligt, før jeg giver en pris.",
      priceLabel: "Pris",
      addonTag: "Valgfrit tilkøb",
      packages: [
        { name: "Spreadsheet Risk Audit", desc: "Teknisk gennemgang af regneark, workflows, forretningslogik, datarisiko, sikkerhedseksponering og rebuild potentiale. Inkluderer en scopet anbefaling og en køreplan for næste skridt." },
        { name: "Secure System Rebuild", desc: "Reverse engineering af kompleks Excel logik, formler, prisregler, afhængigheder og forretningsmekanismer, genopbygget til et sikkert softwaresystem, konfigurator, dashboard eller webapplikation." },
        { name: "AI Workflow & Knowledge Engine", desc: "Virksomhedsspecifikke AI workflows, interne assistenter, procesautomatisering, dokumenthåndtering, strukturerede prompts, integrationer og kontrolleret AI implementering." },
        { name: "Website & Dashboard tilkøb", optional: true, desc: "Ledelsesdashboard, virksomhedsportal eller website koblet til et eksisterende eller genopbygget system. Datakilder, nøgletal og adgang aftales som del af opgaven." },
      ],
      noteMain: "Endelig pris afhænger af kompleksiteten i jeres regneark eller processer, datakilder, integrationer, sikkerhedskrav, brugerroller og valideringsbehov. Større konfiguratorer, produktionssystemer og rapporteringsløsninger prissættes efter vurdering.",
      noteScope: "Alle engagementer starter med en gratis indledende samtale. Fulde rebuilds kræver en scope-vurdering før endelig pris.",
      cta: "Book en Spreadsheet Risk Audit",
      ctaSecondary: "Skriv kort om din situation",
    },
    about: {
      eyebrow: "Om stifteren",
      title: "En der selv bygger og driver systemerne.",
      body:
        "Jeg har specialiseret mig i de regneark, virksomheder er blevet afhængige af. Arbejdet handler ofte også om at forbinde systemer, automatisere manuelle trin og give ledelsen et bedre overblik over tallene. Jeg står selv for det tekniske arbejde, fra at forstå reglerne til at bygge og vedligeholde løsningen.",
      stack: ["AI og agenter", "Reverse engineering", "Sikkert systemdesign", "Web apps og API'er", "Serverdrift", "Sikkerhed og compliance"],
      available: "ledig",
      role: "Stifter, ReverseAI.dk",
      meta: [
        ["fokus", "sikre produktionssystemer"],
        ["region", "EU, remote eller on site"],
        ["levering", "audited, valideret, hærdet"],
      ],
      bottom: "Bygger sikre systemer, der kører i drift",
    },
    sequence: {
      eyebrow: "Transformationen",
      title: "Se et kritisk regneark blive til et sikkert software-system.",
      sub: "Følg ombygningen, ét trin ad gangen. Den kører af sig selv.",
      share: "Del",
      shareCopied: "Link kopieret",
      shareLinkedIn: "Del på LinkedIn",
      steps: [
        { label: "Kilde", caption: "47 ark, udokumenteret. Én person kender logikken." },
        { label: "Kortlæg", caption: "Hver formel sporet ind i en afhængighedsgraf." },
        { label: "Modellér", caption: "Logikken bliver til rene, typede, dokumenterede inputs." },
        { label: "Motor", caption: "Pakket ind i en sikker motor: kryptering, adgang, revision." },
        { label: "Overflade", caption: "Et rent, rollebaseret system. Samme tal, bevist." },
      ],
      stats: [
        { label: "formelafhængigheder" },
        { label: "typede inputs" },
        { label: "afvigelser" },
        { label: "parity" },
      ],
      sourceTag: "47 ark · udokumenteret",
      badges: ["AES-256", "RBAC", "MFA", "Revisionslog"],
      dashTitle: "Sikkert system · live",
    },
    busFactor: {
      eyebrow: "Ingen lock-in",
      title: "\"Hvad hvis du bliver ramt af en bus?\"",
      body: "Det er præcis den risiko, jeg fjerner, så det ville være underligt at give dig en ny. Du ejer den fulde kildekode og dokumentationen, der er ingen lock-in, og hvert system overdrages, så dit eget team, eller enhver anden udvikler, kan køre videre uden mig. Du udskifter ikke ét nøgleperson-problem med et andet. Du afskaffer det.",
      points: ["Fuld kildekode er din", "Komplet dokumentation følger med", "Defineret overdragelse, ingen afhængighed af mig"],
    },
    finalCta: {
      eyebrow: "Sidste spørgsmål",
      title: "Har I et regneark, som styrer mere af virksomheden, end det burde?",
      sub: "Lad os kigge på det sammen. En Spreadsheet Risk Audit giver jer et klart billede af risikoen, hvor hullerne er, og hvad næste skridt bør være.",
      book: "Book en Spreadsheet Risk Audit",
      contact: "Skriv kort om din situation",
    },
    contact: {
      eyebrow: "Kontakt",
      title: "Lad os kigge på jeres system.",
      intro:
        "Fortæl om det regneark, I er afhængige af, den manuelle opgave der tager for lang tid, eller det overblik I mangler. I behøver ikke en færdig kravspecifikation. Jeg vender tilbage med et konkret bud på næste skridt.",
      bookCardTitle: "Vil du hellere tale med det samme?",
      bookCardSub: "Book en gratis introsamtale på 30 minutter om jeres regneark, processer eller rapportering.",
      bookBtn: "Book",
      labels: { name: "Navn", email: "Email", company: "Virksomhed", pkg: "Ydelse", message: "Besked" },
      placeholders: { name: "Dit navn", email: "dig@firma.dk", company: "Firmanavn", message: "Hvad er svært i dag, hvilke systemer er involveret, og hvad vil I gerne ændre?" },
      selectDefault: "Vælg (valgfrit)",
      otherOption: "Andet eller ved ikke endnu",
      submit: "Send forespørgsel",
      sending: "Sender...",
      successTitle: "Tak for din besked",
      successSub: "Jeg vender tilbage hurtigst muligt.",
      sendAnother: "Send en til",
      footnote: "Du hører fra mig inden for 1 til 2 hverdage.",
      errRequired: "Udfyld navn, email og besked.",
      errFail: "Noget gik galt. Prøv igen eller book en audit direkte.",
      successToast: "Tak! Din forespørgsel er sendt. Jeg vender tilbage hurtigst muligt.",
    },
    footer: {
      tagline:
        "Kritiske regneark er mit speciale. Sikre system-rebuilds, procesautomatisering og ledelsesoverblik til B2B-virksomheder, med udvikling og løbende vedligeholdelse samlet ét sted.",
      navHeading: "Navigation",
      getStarted: "Kom i gang",
      book: "Book en Spreadsheet Risk Audit",
      writeToMe: "Skriv til mig",
      role: "Secure Systems & AI Consulting",
    },
    trustbar: {
      label: "Bygget til driftsteams i",
      industries: ["Produktion", "B2B-leverandører", "Ingeniør", "SaaS og tech", "Engros og distribution"],
      points: ["EU-baseret, GDPR-klar", "NDA på forespørgsel", "Valideret til 100% paritet", "Sikkert fra starten"],
    },
    transformation: {
      eyebrow: "Før og efter",
      title: "Fra et skrøbeligt regneark til et system, I kan stole på.",
      intro:
        "Det er den samme forretningslogik, bare bygget om så den er dokumenteret, sikker og testet. Her er, hvad der faktisk ændrer sig for jeres team.",
      beforeLabel: "I dag: kritisk regneark",
      afterLabel: "Efter: sikkert system",
      before: [
        "Logikken lever i hovedet på én person",
        "Skjulte formelfejl når kunderne",
        "Ingen adgangskontrol eller revisionsspor",
        "Følsomme data ligger ubeskyttet",
        "Langsom, manuel tilbudsgivning og pricing",
        "AI kan ikke sikkert røre processen",
      ],
      after: [
        "Dokumenteret logik hele teamet kan stole på",
        "Validerede beregninger, fejl fanges før release",
        "Rollebaseret adgang og fuldt revisionsspor",
        "Kryptering i hvile og i transit",
        "Hurtige, konsistente tilbud og priser",
        "Et rent fundament, AI kan bygge på",
      ],
    },
    liveTransform: {
      eyebrow: "Live system-transformation",
      title: "Se et kritisk regneark blive til et sikkert softwaresystem.",
      intro:
        "Det er den samme proces, jeg kører på rigtige projekter, vist fra start til slut: de skjulte formler i regnearket bliver kortlagt, lavet om til en ren input-model, bygget om til en sikker motor og til sidst givet et dashboard, jeres team kan stole på.",
      live: "live",
      videoSub: "Regnearkslogik lavet om til et sikkert system",
      metricsTitle: "Valideret, ikke antaget",
      stages: [
        { code: "01 kilde", title: "Regneark-formler", desc: "Udokumenterede celler, indlejrede formler og skjult logik.", metric: "47 ark" },
        { code: "02 kortlæg", title: "Dependency graph", desc: "Hver formel og reference kortlagt, også de skjulte.", metric: "1.240 forbindelser" },
        { code: "03 model", title: "Kanonisk input-model", desc: "Inputs typed, valideret og samlet i én ren model.", metric: "62 typed inputs" },
        { code: "04 motor", title: "Sikker softwaremotor", desc: "Forretningsregler genopbygget med kryptering, adgangskontrol og validering.", metric: "AES-256 RBAC" },
        { code: "05 flade", title: "Dashboard & admin-lag", desc: "En sikker, rollebaseret flade hele teamet kan stole på.", metric: "100% paritet" },
      ],
      metrics: [
        { value: "1.240", label: "formel-forbindelser kortlagt" },
        { value: "62", label: "kanoniske inputs" },
        { value: "0", label: "afvigelser efter validering" },
        { value: "100%", label: "beregnings-paritet" },
      ],
    },
    testimonials: {
      eyebrow: "Hvad kunderne siger",
      title: "Betroet med forretningskritiske systemer, og valideret som rigtig software.",
      intro:
        "Anonymiseret feedback fra reelle forløb. Navne er tilbageholdt efter aftale, arbejdet og tallene taler for sig selv.",
      note: "Klientnavne tilbageholdt under NDA.",
      items: [
        { subhead: "Produktionskonfigurator · Fremstilling (~200 ansatte)", metric: "47 ark · 1.240 formelafhængigheder · 62 typede inputs · 0 afvigelser · 100% parity", quote: "Vores prislogik lå i ét enormt regneark, som kun én person forstod. Nu er det et sikkert system, hele teamet kan stole på.", role: "Driftsdirektør", company: "navn under NDA" },
        { subhead: "B2B-leverandør", metric: "Rollebaseret adgang · Fuldt audit-trail · I produktion", quote: "Vi har endelig adgangskontrol og sporbarhed på de tal, der styrer butikken.", role: "CFO", company: "navn under NDA" },
        { subhead: "Engros & distribution", metric: "Revisionsklar · Sikker datahåndtering · Kører live", quote: "Lige til, teknisk og ærligt. Ingen buzzword-demo, bare et system i drift, som revisorerne kunne godkende.", role: "IT-chef", company: "navn under NDA" },
      ],
    },
    faq: {
      eyebrow: "FAQ",
      title: "Spørgsmål virksomheder stiller, før vi starter.",
      intro: "Er dit spørgsmål ikke her, så skriv til mig og få et lige svar.",
      items: [
        { q: "Ejer vi koden og systemet?", a: "Ja. I ejer den fulde kildekode, dokumentationen og det genopbyggede system. Der er ingen lock-in, så I kan tage det med til enhver udvikler." },
        { q: "Holdes alt fortroligt?", a: "Ja. Alt håndteres fortroligt, og jeg underskriver gerne en NDA, før I deler filer eller data." },
        { q: "Hvordan ved jeg, at det nye system matcher mit regneark?", a: "Hvert rebuild valideres mod jeres oprindelige beregninger med en parity-testpakke. I ser beviset: 100% parity, 0 afvigelser, før noget går i drift." },
        { q: "Hvor sikkert er det egentlig?", a: "Ret sikkert. Kryptering i hvile og under transport, multifaktor-login, rollebaseret adgang og revisionsklar logning som standard." },
        { q: "Hvad koster det egentlig?", a: "Et audit fra 35.000 kr. (modregnes i et rebuild). Et fuldt rebuild fra 285.000 kr. afhængigt af kompleksitet. Du får konkret scope og pris efter auditten, ingen overraskelser." },
        { q: "Hvor lang tid tager et projekt?", a: "Et audit er hurtigt. Et fuldt rebuild afhænger af kompleksitet, men du får et klart scope, en tidslinje og en pris først." },
        { q: "Hvad sker der med mine data og GDPR?", a: "Jeg er EU-baseret og bygger med GDPR for øje: dataminimering, adgangskontrol og klar håndtering." },
        { q: "Hvad hvis du bliver ramt af en bus?", a: "Du ejer kildekode og dokumentation, og systemet overdrages, så dit team eller enhver udvikler kan køre videre uden mig." },
        { q: "Hvad hvis jeg ikke ved, hvad jeg har brug for endnu?", a: "Start med den gratis introsamtale. Vi ser på din situation sammen, og du får en konkret anbefaling." },
        { q: "Kan I hjælpe med automatisering eller dashboards uden at bygge et regneark om?", a: "Ja. Kritiske regneark er mit speciale, men jeg løser også opgaver med automatisering og ledelsesoverblik omkring jeres eksisterende systemer. Vi starter med opgaven, de tilgængelige data og hvem der skal bruge resultatet, og aftaler derefter omfanget. Et regnearks-rebuild er ikke en forudsætning." },
      ],
    },
    leadMagnet: {
      eyebrow: "Gratis ressource",
      title: "Få Regneark-risiko-tjeklisten.",
      intro:
        "En praktisk tjekliste til at spotte nøgleperson-risiko, skjulte fejl, sikkerhedshuller og rebuild-potentiale i jeres mest kritiske regneark. Sendt direkte til din indbakke.",
      bullets: ["12 tjek på tværs af logik, sikkerhed og kontinuitet", "Ingen tilmelding, kun din email", "Skrevet til drifts- og økonomiteams"],
      placeholder: "dig@firma.dk",
      companyPlaceholder: "Virksomhed (valgfrit)",
      submit: "Send mig tjeklisten",
      sending: "Sender...",
      successTitle: "Tjek din indbakke",
      successSub: "Regneark-risiko-tjeklisten er på vej. Ser du den ikke, så tjek dit spam-filter.",
      errEmail: "Indtast en gyldig email.",
      errFail: "Noget gik galt. Prøv igen.",
      privacyNote: "Vi bruger kun din email til at sende tjeklisten og lejlighedsvis relevant opfølgning. Afmeld når som helst.",
    },
    privacy: {
      title: "Privatlivs- og cookiepolitik",
      updated: "Senest opdateret juni 2026",
      back: "Tilbage til siden",
      sections: [
        { h: "Hvem vi er", p: "ReverseAI.dk er en enmands teknisk konsulentvirksomhed baseret i EU, drevet af Peter Hald. Du kan kontakte os via formularen på denne side." },
        { h: "Hvad vi indsamler", p: "Når du sender kontaktformularen eller anmoder om tjeklisten, indsamler vi navn, email, virksomhed og besked, du oplyser. Vi indsamler også anonym brugsanalyse, men kun hvis du accepterer analytics-cookies." },
        { h: "Hvorfor vi indsamler det", p: "For at besvare din henvendelse, levere den ressource du bad om, og forbedre websitet. Vi sælger aldrig dine data." },
        { h: "Cookies og analytics", p: "Nødvendige cookies holder siden kørende, fx dit sprogvalg. Valgfri analytics (PostHog) kører først, når du accepterer dem i cookie-banneren. Du kan afvise uden at miste funktionalitet." },
        { h: "Email", p: "Lead- og tjekliste-emails sendes via Resend. Vi bruger kun din email til at svare eller sende det, du bad om. Du kan til enhver tid bede os slette dine data." },
        { h: "Dine rettigheder", p: "Under GDPR kan du anmode om indsigt i, rettelse af eller sletning af dine personoplysninger. Kontakt os via formularen, så handler vi hurtigt." },
      ],
    },
    consent: {
      title: "Vi respekterer dit privatliv",
      body: "Vi bruger nødvendige cookies til at drive siden og valgfri analytics (inkl. PostHog brugsindsigt) til at forstå, hvordan siden bruges, og forbedre den. Du bestemmer over analytics.",
      accept: "Accepter",
      reject: "Afvis",
      necessaryNote: "Nødvendige cookies er altid slået til.",
      privacy: "Privatliv og cookies",
    },
    audit: {
      badge: "Spreadsheet Risk Audit",
      headline: "Book en Spreadsheet Risk Audit",
      sub: "Fortæl os om det regneark, workflow eller den forretningslogik, der styrer mere af jeres virksomhed, end det burde.",
      trust: ["Fortroligt, NDA på forespørgsel", "EU-baseret, GDPR-klar", "Uforpligtende"],
      labels: { name: "Navn", email: "Email", company: "Virksomhed", phone: "Telefon", systemType: "Systemtype", criticality: "Forretningskritikalitet", budget: "Budgetinterval", pkg: "Pakkeinteresse", message: "Besked" },
      optional: "valgfrit",
      placeholders: { name: "Dit navn", email: "dig@firma.dk", company: "Firmanavn", phone: "+45 ...", message: "Beskriv kort det regneark, workflow, konfigurator, prisværktøj eller system, du vil have vurderet." },
      selectDefault: "Vælg",
      systemTypes: [
        { value: "excel_spreadsheet", label: "Excel-regneark" },
        { value: "pricing_calculator", label: "Prisberegner" },
        { value: "product_configurator", label: "Produktkonfigurator" },
        { value: "quote_tool", label: "Tilbudsværktøj" },
        { value: "manual_workflow", label: "Manuelt workflow" },
        { value: "ai_workflow_automation", label: "AI / workflow-automation" },
        { value: "other", label: "Andet" },
      ],
      criticality: [
        { value: "nice_to_have", label: "Rart at have" },
        { value: "important", label: "Vigtigt" },
        { value: "critical", label: "Kritisk for pris/salg/drift" },
      ],
      budget: [
        { value: "below_25k", label: "Under 25.000 DKK" },
        { value: "25k_75k", label: "25.000-75.000 DKK" },
        { value: "75k_200k", label: "75.000-200.000 DKK" },
        { value: "200k_plus", label: "200.000+ DKK" },
        { value: "not_sure", label: "Ikke sikker endnu" },
      ],
      submit: "Anmod om Spreadsheet Risk Audit",
      sending: "Sender...",
      successTitle: "Anmodning modtaget.",
      successBody: "Jeg gennemgår dine systemdetaljer og vender tilbage med næste bedste skridt.",
      successNext: "Hvis projektet passer, er næste skridt en 30-minutters intro-samtale eller en scopet Spreadsheet Risk Audit.",
      successHome: "Tilbage til ReverseAI.dk",
      back: "Tilbage til siden",
      footnote: "Alt, du deler, behandles fortroligt. Du hører fra mig inden for 1-2 hverdage.",
      errRequired: "Udfyld venligst alle påkrævede felter.",
      errEmail: "Indtast en gyldig email.",
      errMessage: "Beskriv venligst dit system lidt mere detaljeret (mindst 20 tegn).",
      errFail: "Noget gik galt. Prøv venligst igen.",
    },
    booking: {
      badge: "Book en tid",
      headline: "Book en 30-minutters samtale",
      sub: "Vælg et tidspunkt, der passer dig, og få en bekræftelse på mail med det samme. Ingen frem og tilbage.",
      trust: ["30 minutter, uforpligtende", "Fortroligt, NDA på forespørgsel", "EU-baseret, GDPR-klar"],
      auditPrompt: "Vil du hellere bare booke en 30-min samtale?",
      steps: { date: "1. Vælg dato", time: "2. Vælg tidspunkt", details: "3. Dine oplysninger" },
      selectDatePrompt: "Vælg en dato for at se ledige tider.",
      loadingSlots: "Henter tider...",
      noSlots: "Ingen ledige tider denne dag. Prøv en anden hverdag.",
      slotsLabel: "Ledige tider (dansk tid)",
      labels: { name: "Navn", email: "Email", company: "Virksomhed", phone: "Telefon", meetingType: "Mødeform", message: "Noget jeg bør vide?" },
      placeholders: { name: "Dit navn", email: "dig@firma.dk", company: "Firmanavn", phone: "+45 ...", message: "Kort: hvad vil du gerne tale om?" },
      meetingTypes: [
        { value: "phone", label: "Telefon" },
        { value: "zoom", label: "Zoom" },
        { value: "teams", label: "Microsoft Teams" },
      ],
      optional: "valgfrit",
      submit: "Bekræft booking",
      sending: "Booker...",
      successTitle: "Din tid er booket",
      successBody: "Din tid er reserveret, og en bekræftelse er på vej til din indbakke. Jeg kontakter dig på det aftalte tidspunkt.",
      successWhen: "Tidspunkt",
      successType: "Mødeform",
      successHome: "Tilbage til ReverseAI.dk",
      back: "Tilbage til siden",
      footnote: "Alle tider er dansk tid (Europe/Copenhagen).",
      errRequired: "Udfyld venligst navn og email.",
      errEmail: "Indtast en gyldig email.",
      errSlot: "Vælg venligst dato og tidspunkt.",
      errType: "Vælg venligst en mødeform.",
      errTaken: "Den tid blev lige taget. Vælg venligst et andet tidspunkt.",
      errFail: "Noget gik galt. Prøv venligst igen.",
    },
  },
};

// Stable, language-independent product names used both in the contact form
// package dropdown and for the "select package" pre-fill from the cards.
export const MAINTENANCE_PACKAGE_NAMES = [
  "Website Care",
  "System Care",
  "Mission-Critical Operations",
];

export const PACKAGE_NAMES = [
  "Spreadsheet Risk Audit",
  "Secure System Rebuild",
  "AI Workflow & Knowledge Engine",
  "Website & Dashboard Add-on",
  ...MAINTENANCE_PACKAGE_NAMES,
];

// Package interest options for the Spreadsheet Risk Audit request form. Names are
// kept aligned with the central pricing/package naming; backend validates
// against this exact set (ALLOWED_PACKAGE_INTEREST in server.py).
export const AUDIT_PACKAGES = [
  "Spreadsheet Risk Audit",
  "Secure System Rebuild",
  "AI Workflow & Knowledge Engine",
  "Website & Dashboard Add-on",
  "Not sure yet",
];
